import { createContext, useCallback, useContext, useEffect, useMemo, useState, useRef, type ReactNode } from 'react';
import { devError } from '../utils/dev-log';
import { collection, query, where, orderBy, limit, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from '../lib/firestore';
import { useProperty } from './PropertyContext';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AppNotification {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error';
  title: string;
  description?: string;
  /** Link to navigate to when clicked */
  link?: string;
  /** Auto-dismiss after ms (0 = never) */
  autoDismissMs?: number;
  createdAt: Date;
  read: boolean;
}

interface NotificationContextValue {
  notifications: AppNotification[];
  unreadCount: number;
  addNotification: (notification: Omit<AppNotification, 'id' | 'createdAt' | 'read'>) => string;
  markRead: (id: string) => void;
  markAllRead: () => void;
  dismissNotification: (id: string) => void;
  clearAll: () => void;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const NotificationContext = createContext<NotificationContextValue | null>(null);

let notifCounter = 0;

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const seenEventsRef = useRef<Set<string>>(new Set());

  const addNotification = useCallback(
    (notif: Omit<AppNotification, 'id' | 'createdAt' | 'read'>): string => {
      const id = `notif-${++notifCounter}-${Date.now()}`;
      const newNotif: AppNotification = {
        ...notif,
        id,
        createdAt: new Date(),
        read: false,
      };

      setNotifications((prev) => [newNotif, ...prev].slice(0, 50));

      if (notif.autoDismissMs && notif.autoDismissMs > 0) {
        const timer = setTimeout(() => {
          setNotifications((prev) => prev.filter((n) => n.id !== id));
          timersRef.current.delete(id);
        }, notif.autoDismissMs);
        timersRef.current.set(id, timer);
      }

      return id;
    },
    [],
  );

  const markRead = useCallback((id: string) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
  }, []);

  const markAllRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, []);

  const dismissNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
    for (const timer of timersRef.current.values()) clearTimeout(timer);
    timersRef.current.clear();
  }, []);

  // Listen to admin_events for real-time alerts
  const { selectedProperty } = useProperty();
  useEffect(() => {
    if (!selectedProperty?.id) return;

    // Reset seen events when property changes to prevent unbounded growth
    seenEventsRef.current.clear();

    const since = Timestamp.fromDate(new Date(Date.now() - 24 * 60 * 60 * 1000));
    const eventsRef = collection(db, 'properties', selectedProperty.id, 'admin_events');
    const q = query(
      eventsRef,
      where('timestamp', '>=', since),
      orderBy('timestamp', 'desc'),
      limit(20),
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      for (const change of snapshot.docChanges()) {
        if (change.type !== 'added') continue;
        const id = change.doc.id;
        if (seenEventsRef.current.has(id)) continue;
        seenEventsRef.current.add(id);

        const data = change.doc.data();
        const eventType = data.type as string;

        if (eventType === 'lead_captured') {
          addNotification({
            type: 'success',
            title: `New lead: ${data.guestName || data.guestEmail || 'Anonymous'}`,
            description: data.guestEmail ? `Email: ${data.guestEmail}` : undefined,
            link: `/properties/${selectedProperty.id}/sessions`,
            autoDismissMs: 15000,
          });
        } else if (eventType === 'reservation_linked') {
          addNotification({
            type: 'info',
            title: `Reservation linked: ${data.confirmationNumber || ''}`,
            description: data.guestName ? `Guest: ${data.guestName}` : undefined,
            link: `/properties/${selectedProperty.id}/sessions`,
            autoDismissMs: 15000,
          });
        } else if (eventType === 'handover_requested') {
          const priorityLabel = data.priority === 'urgent' ? ' (URGENT)' : data.priority === 'high' ? ' (High)' : '';
          addNotification({
            type: data.priority === 'urgent' || data.priority === 'high' ? 'error' : 'warning',
            title: `New handover request${priorityLabel}`,
            description: data.reason || 'A guest is requesting human assistance',
            link: `/properties/${selectedProperty.id}/handovers`,
            autoDismissMs: 0, // Do not auto-dismiss handover alerts
          });
        }
      }
    }, (error) => {
      // Permission denied is expected for users without legacy admin doc
      if (error.code !== 'permission-denied') {
        devError('Admin events listener error:', error);
      }
    });

    return () => unsubscribe();
  }, [selectedProperty?.id, addNotification]);

  const unreadCount = useMemo(() => notifications.filter((n) => !n.read).length, [notifications]);

  const value = useMemo(
    () => ({ notifications, unreadCount, addNotification, markRead, markAllRead, dismissNotification, clearAll }),
    [notifications, unreadCount, addNotification, markRead, markAllRead, dismissNotification, clearAll],
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications(): NotificationContextValue {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationProvider');
  return ctx;
}
